/**
 * Editor Interaction Service
 * Consolidated service combining selection, cursor navigation, tag interaction, and cursor preservation
 * Handles all editor interactions including clicks, keyboard navigation, selection, and cursor preservation
 */

import { inject, Injectable } from '@angular/core'
import {
  CSS_CLASSES,
  CURSOR_PRESERVATION_DELAYS,
  DEBUG_MESSAGES,
  DOM_ELEMENT_NAMES,
  HTML_ATTRIBUTES,
  HTML_VALUES,
  MUTATION_OBSERVER_CONFIG,
  NODE_TYPES,
  TAG_EDITOR_STRINGS,
} from '../tag-editor.constants'
import { InlineEditingState } from '../tag-editor.model'
import { DebugLoggingService } from './debug-logging.service'

interface CursorState {
  readonly range: Range
  readonly container: Node
  readonly offset: number
}

interface ObserverWithCleanup {
  readonly observer: MutationObserver
  readonly inputHandler: () => void
  readonly beforeInputHandler: () => void
}

interface TagElementAndRange {
  readonly tagElement: HTMLElement
  readonly range: Range
}

/**
 * Rich selection state that includes both plain text and HTML content (DocumentFragment)
 * This preserves formatting (bold, italic, underline, line breaks) when inserting tags
 */
export interface RichSelectionState {
  readonly hasSelection: boolean
  readonly selectedText: string
  readonly isCollapsed: boolean
  readonly clonedContent: DocumentFragment | null
  readonly range: Range | null
}

@Injectable({ providedIn: 'root' })
export class EditorInteractionService {
  private readonly debugLoggingService = inject(DebugLoggingService);

  private activeObservers: ReadonlyMap<HTMLElement, ObserverWithCleanup> = new Map()
  private isUserInputInProgress: ReadonlyMap<HTMLElement, boolean> = new Map()
  private restoreDebounceTimeout: number | null = null

  // ========== Selection Methods ==========

  getRichSelectionState(): RichSelectionState {
    const selection: Selection | null = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      this.debugLoggingService.log('EditorInteractionService.getRichSelectionState():', {
        hasSelection: false,
        text: TAG_EDITOR_STRINGS.EMPTY_STRING,
        length: 0,
      })
      return {
        hasSelection: false,
        selectedText: TAG_EDITOR_STRINGS.EMPTY_STRING,
        isCollapsed: true,
        clonedContent: null,
        range: null,
      }
    }

    const range: Range = selection.getRangeAt(0)
    const isCollapsed: boolean = range.collapsed
    const text: string = selection.toString()
    const clonedContent: DocumentFragment = range.cloneContents()

    this.debugLoggingService.log('EditorInteractionService.getRichSelectionState():', {
      hasSelection: true,
      isCollapsed,
      text,
      textLength: text.length,
      rangeCount: selection.rangeCount,
      startContainer: range.startContainer.nodeName,
      endContainer: range.endContainer.nodeName,
      startOffset: range.startOffset,
      endOffset: range.endOffset,
      clonedContentNodes: clonedContent.childNodes.length,
    })

    return {
      hasSelection: true,
      selectedText: text,
      isCollapsed,
      clonedContent,
      range: range.cloneRange(), // Clone range for safety
    }
  }

  getSelectedText(): string {
    return this.getRichSelectionState().selectedText
  }

  saveSelection(): Range | null {
    const selection: Selection | null = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range: Range = selection.getRangeAt(0).cloneRange()
      this.debugLoggingService.log('EditorInteractionService.saveSelection():', {
        hasRange: !!range,
        rangeCount: selection.rangeCount,
        selectedText: selection.toString(),
      })
      return range
    }
    this.debugLoggingService.logWithService('EditorInteractionService', 'saveSelection(): No selection found')
    return null
  }

  restoreSelection(range: Range): void {
    const selection: Selection | null = window.getSelection()
    if (selection) {
      selection.removeAllRanges()
      selection.addRange(range)
      this.debugLoggingService.logWithService('EditorInteractionService', 'restoreSelection():', {
        restored: true,
        selectedText: selection.toString(),
      })
    } else {
      this.debugLoggingService.logWithService('EditorInteractionService', 'restoreSelection(): No selection object')
    }
  }

  // ========== Cursor Navigation Methods ==========

  handleBackspaceKey(
    event: KeyboardEvent,
    currentlyEditingTag: InlineEditingState | null,
    editorContentRef: HTMLElement | null,
    onExitInlineEditMode: (save: boolean) => void,
    onMoveCursorBeforeTag: (tagElement: HTMLElement) => void,
  ): boolean {
    const tagElementAndRange: TagElementAndRange | null = this.getTagElementAndRange(editorContentRef)
    if (!tagElementAndRange) {
      return false
    }

    const { tagElement, range } = tagElementAndRange

    const isEditable: boolean =
      tagElement.getAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE) === HTML_VALUES.CONTENTEDITABLE_TRUE
    if (!isEditable) {
      return false
    }

    const isAtStart: boolean = this.isCursorAtStartOfTag(tagElement, range)
    if (!isAtStart) {
      return false
    }

    this.debugLoggingService.logWithService(
      'EditorInteractionService',
      'Backspace key detected at start of tag, moving cursor outside',
    )

    event.preventDefault()
    event.stopPropagation()

    if (currentlyEditingTag && currentlyEditingTag.element === tagElement) {
      this.debugLoggingService.logWithService(
        'EditorInteractionService',
        'Exiting inline edit mode before moving cursor outside',
      )
      onExitInlineEditMode(true)
    }

    onMoveCursorBeforeTag(tagElement)
    return true
  }

  handleSpaceKey(
    event: KeyboardEvent,
    editorContentRef: HTMLElement | null,
    onMoveCursorOutsideTag: (tagElement: HTMLElement) => void,
  ): boolean {
    const tagElementAndRange: TagElementAndRange | null = this.getTagElementAndRange(editorContentRef)
    if (!tagElementAndRange) {
      return false
    }

    const { tagElement, range } = tagElementAndRange

    const isEditable: boolean =
      tagElement.getAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE) === HTML_VALUES.CONTENTEDITABLE_TRUE
    if (isEditable) {
      return false
    }

    const isAtAbsoluteEnd: boolean = this.isCursorAtAbsoluteEndOfTag(tagElement, range)
    if (!isAtAbsoluteEnd) {
      return false
    }

    this.debugLoggingService.logWithService(
      'EditorInteractionService',
      'Space intercepted: non-editable tag at absolute end, moving cursor outside',
    )

    event.preventDefault()
    event.stopPropagation()
    onMoveCursorOutsideTag(tagElement)
    return true
  }

  handleArrowRightKey(
    event: KeyboardEvent,
    editorContentRef: HTMLElement | null,
    onMoveCursorOutsideTag: (tagElement: HTMLElement) => void,
  ): boolean {
    return this.handleArrowKeyNavigation(
      event,
      editorContentRef,
      (tagElement: HTMLElement, range: Range) => this.isCursorAtAbsoluteEndOfTag(tagElement, range),
      DEBUG_MESSAGES.ARROW_RIGHT_DETECTED,
      DEBUG_MESSAGES.CURSOR_NOT_AT_END,
      DEBUG_MESSAGES.CURSOR_AT_END,
      onMoveCursorOutsideTag,
    )
  }

  handleArrowLeftKey(
    event: KeyboardEvent,
    editorContentRef: HTMLElement | null,
    onMoveCursorBeforeTag: (tagElement: HTMLElement) => void,
  ): boolean {
    return this.handleArrowKeyNavigation(
      event,
      editorContentRef,
      (tagElement: HTMLElement, range: Range) => this.isCursorAtStartOfTag(tagElement, range),
      DEBUG_MESSAGES.ARROW_LEFT_DETECTED,
      DEBUG_MESSAGES.CURSOR_NOT_AT_START,
      DEBUG_MESSAGES.CURSOR_AT_START,
      onMoveCursorBeforeTag,
    )
  }

  createRangeFromPoint(x: number, y: number, editorContentRef: HTMLElement | null): Range | null {
    if (!editorContentRef) {
      return null
    }

    if (typeof document.caretPositionFromPoint === 'function') {
      const caretPosition: CaretPosition | null = document.caretPositionFromPoint(x, y)
      if (caretPosition && caretPosition.offsetNode) {
        if (editorContentRef.contains(caretPosition.offsetNode)) {
          const range: Range = document.createRange()
          range.setStart(caretPosition.offsetNode, caretPosition.offset)
          range.setEnd(caretPosition.offsetNode, caretPosition.offset)
          return range
        }
      }
    }

    const walker: TreeWalker = document.createTreeWalker(editorContentRef, NodeFilter.SHOW_TEXT, null)

    let closestNode: Node | null = null
    let closestDistance: number = Infinity

    let node: Node | null = walker.nextNode()
    while (node) {
      const range: Range = document.createRange()
      range.selectNodeContents(node)
      const nodeRect: DOMRect = range.getBoundingClientRect()

      if (nodeRect.left <= x && x <= nodeRect.right && nodeRect.top <= y && y <= nodeRect.bottom) {
        const textNode: Text = node as Text
        const textContent: string = textNode.textContent || ''
        if (textContent.length > 0) {
          const charWidth: number = nodeRect.width / textContent.length
          const offset: number = Math.floor((x - nodeRect.left) / charWidth)
          const clampedOffset: number = Math.max(0, Math.min(offset, textContent.length))

          const finalRange: Range = document.createRange()
          finalRange.setStart(textNode, clampedOffset)
          finalRange.setEnd(textNode, clampedOffset)
          return finalRange
        }
      }

      const centerX: number = nodeRect.left + nodeRect.width / 2
      const centerY: number = nodeRect.top + nodeRect.height / 2
      const distance: number = Math.sqrt(Math.pow(centerX - x, 2) + Math.pow(centerY - y, 2))
      if (distance < closestDistance) {
        closestDistance = distance
        closestNode = node
      }

      node = walker.nextNode()
    }

    if (closestNode && closestNode.nodeType === NODE_TYPES.TEXT_NODE) {
      const range: Range = document.createRange()
      range.setStart(closestNode, 0)
      range.setEnd(closestNode, 0)
      return range
    }

    if (editorContentRef.firstChild) {
      const range: Range = document.createRange()
      range.setStart(editorContentRef, 0)
      range.collapse(true)
      return range
    }

    return null
  }

  findTagElement(node: Node, editorContentRef: HTMLElement | null): HTMLElement | null {
    if (node.nodeType === NODE_TYPES.ELEMENT_NODE) {
      const element: HTMLElement = node as HTMLElement
      if (element.classList.contains(CSS_CLASSES.TAG_ANCHOR)) {
        return element
      }
    }

    let current: Node | null = node
    while (current && current !== editorContentRef) {
      if (current.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const element: HTMLElement = current as HTMLElement
        if (element.classList.contains(CSS_CLASSES.TAG_ANCHOR)) {
          return element
        }
      }
      current = current.parentNode
    }

    return null
  }

  // ========== Tag Interaction Methods ==========

  positionCursorAfterTag(tagElement: HTMLElement): boolean {
    const selection: Selection | null = window.getSelection()
    if (!selection) {
      this.debugLoggingService.log('EditorInteractionService.positionCursorAfterTag: No selection object')
      return false
    }

    try {
      const range: Range = document.createRange()
      const nextNode: Node | null = this.findNextEditableNode(tagElement)

      if (nextNode && nextNode.nodeType === NODE_TYPES.TEXT_NODE) {
        range.setStart(nextNode, 0)
        range.setEnd(nextNode, 0)
      } else if (nextNode && nextNode.nodeType === NODE_TYPES.ELEMENT_NODE) {
        range.setStart(nextNode, 0)
        range.setEnd(nextNode, 0)
      } else {
        const parentNode: Node | null = tagElement.parentNode
        if (parentNode) {
          const tagIndex: number = Array.from(parentNode.childNodes).indexOf(tagElement)
          if (tagIndex >= 0 && tagIndex < parentNode.childNodes.length - 1) {
            const nextSibling: Node = parentNode.childNodes[tagIndex + 1]
            range.setStart(nextSibling, 0)
            range.setEnd(nextSibling, 0)
          } else {
            range.setStart(parentNode, parentNode.childNodes.length)
            range.setEnd(parentNode, parentNode.childNodes.length)
          }
        } else {
          range.setStartAfter(tagElement)
          range.setEndAfter(tagElement)
        }
      }

      selection.removeAllRanges()
      selection.addRange(range)

      this.debugLoggingService.log('EditorInteractionService.positionCursorAfterTag: Cursor positioned after tag', {
        tagId: tagElement.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID),
        nextNodeType: nextNode?.nodeType,
        nextNodeName: nextNode?.nodeName,
      })

      return true
    } catch (error) {
      this.debugLoggingService.log('EditorInteractionService.positionCursorAfterTag: Failed to position cursor', error)
      return false
    }
  }

  positionCursorBeforeTag(tagElement: HTMLElement): boolean {
    const selection: Selection | null = window.getSelection()
    if (!selection) {
      return false
    }

    try {
      const range: Range = document.createRange()
      const previousNode: Node | null = this.findPreviousEditableNode(tagElement)

      if (previousNode && previousNode.nodeType === NODE_TYPES.TEXT_NODE) {
        const textLength: number = previousNode.textContent?.length || 0
        range.setStart(previousNode, textLength)
        range.setEnd(previousNode, textLength)
      } else if (previousNode && previousNode.nodeType === NODE_TYPES.ELEMENT_NODE) {
        if (previousNode.lastChild && previousNode.lastChild.nodeType === NODE_TYPES.TEXT_NODE) {
          const textNode: Text = previousNode.lastChild as Text
          const textLength: number = textNode.textContent?.length || 0
          range.setStart(textNode, textLength)
          range.setEnd(textNode, textLength)
        } else {
          range.setStart(previousNode, previousNode.childNodes.length)
          range.setEnd(previousNode, previousNode.childNodes.length)
        }
      } else {
        const parentNode: Node | null = tagElement.parentNode
        if (parentNode) {
          const tagIndex: number = Array.from(parentNode.childNodes).indexOf(tagElement)
          if (tagIndex > 0) {
            const previousSibling: Node = parentNode.childNodes[tagIndex - 1]
            if (previousSibling.nodeType === NODE_TYPES.TEXT_NODE) {
              const textLength: number = previousSibling.textContent?.length || 0
              range.setStart(previousSibling, textLength)
              range.setEnd(previousSibling, textLength)
            } else {
              range.setStart(previousSibling, previousSibling.childNodes.length)
              range.setEnd(previousSibling, previousSibling.childNodes.length)
            }
          } else {
            range.setStart(parentNode, 0)
            range.setEnd(parentNode, 0)
          }
        } else {
          range.setStartBefore(tagElement)
          range.setEndBefore(tagElement)
        }
      }

      selection.removeAllRanges()
      selection.addRange(range)

      this.debugLoggingService.log('EditorInteractionService.positionCursorBeforeTag: Cursor positioned before tag', {
        tagId: tagElement.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID),
      })

      return true
    } catch (error) {
      this.debugLoggingService.log('EditorInteractionService.positionCursorBeforeTag: Failed to position cursor', error)
      return false
    }
  }

  // ========== Cursor Preservation Methods ==========

  startPreservingCursor(element: HTMLElement): void {
    this.stopPreservingCursor(element)

    let savedCursorState: CursorState | null = null

    const saveCursorState = (): void => {
      const selection: Selection | null = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        savedCursorState = null
        return
      }

      try {
        const range: Range = selection.getRangeAt(0).cloneRange()
        savedCursorState = {
          range,
          container: range.startContainer,
          offset: range.startOffset,
        }
      } catch (error) {
        this.debugLoggingService.logWithService('EditorInteractionService', 'Could not save cursor state:', error)
        savedCursorState = null
      }
    }

    const restoreCursorState = (): void => {
      if (!savedCursorState) {
        return
      }

      const selection: Selection | null = window.getSelection()
      if (!selection) {
        return
      }

      try {
        if (!document.contains(savedCursorState.container) || !element.contains(savedCursorState.container)) {
          savedCursorState = null
          return
        }

        const textLength: number =
          savedCursorState.container.nodeType === NODE_TYPES.TEXT_NODE
            ? (savedCursorState.container as Text).textContent?.length || 0
            : savedCursorState.container.childNodes.length

        if (savedCursorState.offset > textLength) {
          savedCursorState = null
          return
        }

        const range: Range = document.createRange()
        const adjustedOffset: number = Math.min(savedCursorState.offset, textLength)
        range.setStart(savedCursorState.container, adjustedOffset)
        range.collapse(true)

        selection.removeAllRanges()
        selection.addRange(range)

        if (document.activeElement !== element && element.isConnected) {
          element.focus()
        }
      } catch (error) {
        this.debugLoggingService.logWithService('EditorInteractionService', 'Could not restore cursor state:', error)
        savedCursorState = null
      }
    }

    saveCursorState()

    const observer: MutationObserver = new MutationObserver(() => {
      if (this.isUserInputInProgress.get(element)) {
        saveCursorState()
        return
      }

      if (this.restoreDebounceTimeout !== null) {
        clearTimeout(this.restoreDebounceTimeout)
      }

      this.restoreDebounceTimeout = window.setTimeout(() => {
        const selection: Selection | null = window.getSelection()
        const isCursorLost: boolean =
          !selection || selection.rangeCount === 0 || !element.contains(selection.anchorNode)

        if (isCursorLost && savedCursorState) {
          restoreCursorState()
        }
        saveCursorState()
        this.restoreDebounceTimeout = null
      }, CURSOR_PRESERVATION_DELAYS.RESTORE_DEBOUNCE_MS)
    })

    observer.observe(element, {
      childList: MUTATION_OBSERVER_CONFIG.CHILD_LIST,
      characterData: MUTATION_OBSERVER_CONFIG.CHARACTER_DATA,
      subtree: MUTATION_OBSERVER_CONFIG.SUBTREE,
      attributes: MUTATION_OBSERVER_CONFIG.ATTRIBUTES,
    })

    const inputHandler = (): void => {
      this.isUserInputInProgress = new Map(this.isUserInputInProgress).set(element, true)
      saveCursorState()
      setTimeout(() => {
        this.isUserInputInProgress = new Map(this.isUserInputInProgress).set(element, false)
      }, CURSOR_PRESERVATION_DELAYS.USER_INPUT_FLAG_CLEAR_MS)
    }
    element.addEventListener('input', inputHandler)

    const beforeInputHandler = (): void => {
      this.isUserInputInProgress = new Map(this.isUserInputInProgress).set(element, true)
    }
    element.addEventListener('beforeinput', beforeInputHandler)

    const observerWithCleanup: ObserverWithCleanup = {
      observer,
      inputHandler,
      beforeInputHandler,
    }
    this.activeObservers = new Map(this.activeObservers).set(element, observerWithCleanup)
    this.isUserInputInProgress = new Map(this.isUserInputInProgress).set(element, false)
  }

  stopPreservingCursor(element: HTMLElement): void {
    const storedObserver: ObserverWithCleanup | undefined = this.activeObservers.get(element)
    if (!storedObserver) {
      return
    }

    if (this.restoreDebounceTimeout !== null) {
      clearTimeout(this.restoreDebounceTimeout)
      this.restoreDebounceTimeout = null
    }

    storedObserver.observer.disconnect()
    element.removeEventListener('input', storedObserver.inputHandler)
    element.removeEventListener('beforeinput', storedObserver.beforeInputHandler)

    const newObservers: Map<HTMLElement, ObserverWithCleanup> = new Map(this.activeObservers)
    newObservers.delete(element)
    this.activeObservers = newObservers

    const newInputFlags: Map<HTMLElement, boolean> = new Map(this.isUserInputInProgress)
    newInputFlags.delete(element)
    this.isUserInputInProgress = newInputFlags
  }

  stopAll(): void {
    if (this.restoreDebounceTimeout !== null) {
      clearTimeout(this.restoreDebounceTimeout)
      this.restoreDebounceTimeout = null
    }

    this.activeObservers.forEach((_observer: ObserverWithCleanup, element: HTMLElement) => {
      this.stopPreservingCursor(element)
    })
  }

  // ========== Private Helper Methods ==========

  private handleArrowKeyNavigation(
    event: KeyboardEvent,
    editorContentRef: HTMLElement | null,
    checkCursorPosition: (tagElement: HTMLElement, range: Range) => boolean,
    detectedMessage: string,
    notAtPositionMessage: string,
    atPositionMessage: string,
    onMoveCursor: (tagElement: HTMLElement) => void,
  ): boolean {
    const selection: Selection | null = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }

    const range: Range = selection.getRangeAt(0)
    const focusNode: Node | null = selection.focusNode

    const tagElement: HTMLElement | null = this.findTagElement(focusNode!, editorContentRef)
    if (!tagElement) {
      return false
    }

    this.debugLoggingService.log(detectedMessage, tagElement.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID))

    const isAtPosition: boolean = checkCursorPosition(tagElement, range)
    if (!isAtPosition) {
      this.debugLoggingService.log(notAtPositionMessage)
      return false
    }

    this.debugLoggingService.log(atPositionMessage)

    event.preventDefault()
    onMoveCursor(tagElement)
    return true
  }

  private isCursorAtAbsoluteEndOfTag(tagElement: HTMLElement, range: Range): boolean {
    const focusNode: Node = range.endContainer
    const focusOffset: number = range.endOffset

    if (focusNode.nodeType === NODE_TYPES.ELEMENT_NODE) {
      const element: HTMLElement = focusNode as HTMLElement
      if (element.parentElement === tagElement || element === tagElement) {
        return false
      }
    }

    if (focusNode.nodeType === NODE_TYPES.TEXT_NODE) {
      const textContent: string = focusNode.textContent || ''
      const normalizedText: string = textContent.replace(
        new RegExp(TAG_EDITOR_STRINGS.NON_BREAKING_SPACE, 'g'),
        TAG_EDITOR_STRINGS.SPACE,
      )
      const visibleTextLength: number = normalizedText.trimEnd().length

      if (focusOffset < visibleTextLength) {
        return false
      }

      const nextSibling: Node | null = focusNode.nextSibling
      if (nextSibling && nextSibling.nodeType === NODE_TYPES.ELEMENT_NODE) {
        return false
      }

      const previousSibling: Node | null = focusNode.previousSibling
      if (previousSibling && previousSibling.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const prevElement: HTMLElement = previousSibling as HTMLElement
        if (prevElement.tagName === DOM_ELEMENT_NAMES.BR) {
          return false
        }
      }

      if (focusNode.parentElement === tagElement) {
        const childIndex: number = Array.from(tagElement.childNodes).indexOf(focusNode as ChildNode)
        if (childIndex < tagElement.childNodes.length - 1) {
          return false
        }
      }

      return true
    }

    if (focusNode === tagElement) {
      const childCount: number = tagElement.childNodes.length
      if (focusOffset < childCount) {
        return false
      }
      if (childCount > 0) {
        const lastChild: Node = tagElement.childNodes[childCount - 1]
        if (lastChild.nodeType === NODE_TYPES.ELEMENT_NODE) {
          return false
        }
      }
      return true
    }

    return false
  }

  private isCursorAtStartOfTag(tagElement: HTMLElement, range: Range): boolean {
    const focusNode: Node = range.endContainer
    const focusOffset: number = range.endOffset

    if (focusNode.nodeType === NODE_TYPES.TEXT_NODE && focusNode.parentElement === tagElement) {
      return focusOffset === 0
    }

    if (focusNode === tagElement) {
      return focusOffset === 0
    }

    if (focusNode.parentElement === tagElement) {
      const childIndex: number = Array.from(tagElement.childNodes).indexOf(focusNode as ChildNode)
      const isFirstChild: boolean = childIndex === 0
      return isFirstChild && focusOffset === 0
    }

    return false
  }

  private getTagElementAndRange(editorContentRef: HTMLElement | null): TagElementAndRange | null {
    const selection: Selection | null = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return null
    }

    const range: Range = selection.getRangeAt(0)
    const focusNode: Node = range.endContainer

    const tagElement: HTMLElement | null = this.findTagElement(focusNode, editorContentRef)
    if (!tagElement) {
      return null
    }

    return { tagElement, range }
  }

  private findNextEditableNode(tagElement: HTMLElement): Node | null {
    let current: Node | null = tagElement.nextSibling

    while (current) {
      if (current.nodeType === NODE_TYPES.TEXT_NODE) {
        return current
      }

      if (current.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const element: HTMLElement = current as HTMLElement
        if (!element.classList.contains(CSS_CLASSES.TAG_ANCHOR)) {
          const firstTextNode: Node | null = this.findFirstTextNode(element)
          if (firstTextNode) {
            return firstTextNode
          }
          return element
        }
      }
      current = current.nextSibling
    }

    const parent: Node | null = tagElement.parentNode
    if (parent && parent.parentNode) {
      const parentNodes: ReadonlyArray<Node> = Array.from(parent.parentNode.childNodes)
      const parentIndex: number = parentNodes.indexOf(parent)
      if (parentIndex >= 0 && parentIndex < parentNodes.length - 1) {
        return parentNodes[parentIndex + 1]
      }
    }

    return null
  }

  private findPreviousEditableNode(tagElement: HTMLElement): Node | null {
    let current: Node | null = tagElement.previousSibling

    while (current) {
      if (current.nodeType === NODE_TYPES.TEXT_NODE) {
        return current
      }

      if (current.nodeType === NODE_TYPES.ELEMENT_NODE) {
        const element: HTMLElement = current as HTMLElement
        if (!element.classList.contains(CSS_CLASSES.TAG_ANCHOR)) {
          const lastTextNode: Node | null = this.findLastTextNode(element)
          if (lastTextNode) {
            return lastTextNode
          }
          return element
        }
      }
      current = current.previousSibling
    }

    const parent: Node | null = tagElement.parentNode
    if (parent && parent.parentNode) {
      const parentNodes: ReadonlyArray<Node> = Array.from(parent.parentNode.childNodes)
      const parentIndex: number = parentNodes.indexOf(parent)
      if (parentIndex > 0) {
        return parentNodes[parentIndex - 1]
      }
    }

    return null
  }

  private findFirstTextNode(element: Node): Node | null {
    if (element.nodeType === NODE_TYPES.TEXT_NODE) {
      return element
    }

    for (let i = 0; i < element.childNodes.length; i++) {
      const child: Node = element.childNodes[i]
      const textNode: Node | null = this.findFirstTextNode(child)
      if (textNode) {
        return textNode
      }
    }

    return null
  }

  private findLastTextNode(element: Node): Node | null {
    if (element.nodeType === NODE_TYPES.TEXT_NODE) {
      return element
    }

    for (let i = element.childNodes.length - 1; i >= 0; i--) {
      const child: Node = element.childNodes[i]
      const textNode: Node | null = this.findLastTextNode(child)
      if (textNode) {
        return textNode
      }
    }

    return null
  }
}
